import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import { getAllEvents } from "./EventServices";
import { EntitiesListResponseModel } from "../../utilities/models/EntitiesListResponseModel";
import EventPreviewCard from "./EventPreviewCard";
import { Link } from "react-router-dom";
import { CONTACT_PAGE_URL } from "../../utilities/constants/PagesEndpoints";
import { Calendar, Sparkles } from "lucide-react";

const EventsPage = () => {
  const [events, setEvents] = useState(EntitiesListResponseModel);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getAllEvents((data) => {
      setEvents(data);
      setIsLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      {/* Hero Section */}
      <div className="relative bg-slate-900 py-24 sm:py-32 overflow-hidden isolating">
        {/* Background Effects */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 opacity-90" />
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent to-slate-900/50" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-sm border border-white/10 shadow-xl">
              <Sparkles className="w-8 h-8 text-yellow-300" />
            </div>
          </div>
          <h1 className="text-4xl md:text-6xl font-serif font-bold tracking-tight text-white mb-6 drop-shadow-lg">
            Unforgettable Moments
          </h1>
          <p className="mt-4 text-lg md:text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed font-light">
            Explore our gallery of past celebrations. See how our venue
            transforms to bring unique visions to life, creating memories that
            last a lifetime.
          </p>
          <div className="mt-10 flex justify-center gap-4">
            <Link
              to={CONTACT_PAGE_URL}
              className="rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-slate-900 shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all transform hover:-translate-y-1"
            >
              Start Your Journey
            </Link>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="h-96 rounded-2xl bg-slate-200 dark:bg-slate-800 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <section>
            {events.content && events.content.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {events.content.map((event, index) => (
                  <div key={`${event.title}-${index}`} className="group h-full">
                    <div className="h-full transform transition-all duration-300 hover:-translate-y-2">
                      <EventPreviewCard event={event} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Empty State */
              <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                <div className="w-24 h-24 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-6">
                  <Calendar className="w-10 h-10 text-slate-400 dark:text-slate-500" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  Upcoming Events
                </h3>
                <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto mb-8">
                  We're currently curating our next series of events. Check back
                  soon for updates on our latest gatherings and celebrations.
                </p>
                <Link
                  to={CONTACT_PAGE_URL}
                  className="text-blue-600 dark:text-blue-400 font-semibold hover:text-blue-700 dark:hover:text-blue-300"
                >
                  Contact us for inquiries &rarr;
                </Link>
              </div>
            )}
          </section>
        )}
      </div>
      <ToastContainer position="bottom-right" theme="colored" />
    </div>
  );
};

export default EventsPage;
